import { type SharedContentItem, useStores } from '@littlebird/sdk';
import {
  formatRelativeDate,
  sharedItemAuthorLabel,
  shouldShowSharedItemAuthors,
} from '@littlebird/ui-utils';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, Skeleton, XStack, YStack } from '@/components/ui';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { useRenameField } from '@/components/ui/RenameField/useRenameField';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { ProjectEmptyState } from './project-empty-state';
import { ProjectItemRowActions } from './project-item-row-actions';
import { useProjectMembers } from './project-members.query';

interface ProjectSharedItemsListProps {
  projectId: string;
  currentUserId: number;
  itemType?: 'chat' | 'meeting';
  testIdPrefix: string;
  emptyTitle: string;
  emptyDescription: string;
  ownHref: (item: SharedContentItem) => string;
  data: SharedContentItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export const ProjectSharedItemsList = observer(function ProjectSharedItemsList({
  projectId,
  currentUserId,
  testIdPrefix,
  emptyTitle,
  emptyDescription,
  ownHref,
  data,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: ProjectSharedItemsListProps) {
  const navigate = useNavigate();
  const { projects } = useStores();
  const { data: members } = useProjectMembers(projectId);
  const isSharedProject = shouldShowSharedItemAuthors({
    hasShareLink: projects.getById(projectId)?.hasShareLink === true,
    memberCount: members?.length ?? null,
    items: data,
    currentUserId,
  });

  const sharedHref = (item: SharedContentItem) =>
    `/projects/${projectId}/shared/${item.type}/${item.id}`;

  const openItem = (item: SharedContentItem) => {
    if (item.authorUserId === currentUserId) {
      navigate({ to: ownHref(item) });
    } else {
      navigate({
        to: '/projects/$projectId/shared/$itemType/$itemId',
        params: { projectId, itemType: item.type, itemId: item.id },
      });
    }
  };

  if (isLoading) {
    return (
      <YStack className="gap-2">
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-lg" />
        ))}
      </YStack>
    );
  }

  if (data.length === 0) {
    return (
      <YStack className="pt-12">
        <ProjectEmptyState title={emptyTitle} description={emptyDescription} />
      </YStack>
    );
  }

  return (
    <YStack className="gap-2">
      {data.map((item) => {
        const isOwn = item.authorUserId === currentUserId;
        return (
          <ProjectSharedItemRow
            key={item.id}
            item={item}
            isOwn={isOwn}
            showAvatar={isSharedProject}
            testIdPrefix={testIdPrefix}
            href={isOwn ? ownHref(item) : sharedHref(item)}
            onOpen={() => openItem(item)}
          />
        );
      })}
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="self-center"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </YStack>
  );
});

const isRenameInput = (target: EventTarget) =>
  target instanceof HTMLInputElement;

const TYPE_ICONS = {
  chat: Icons.speech,
  meeting: Icons.meetings,
} as const;

function ItemTypeAvatar({
  item,
  showAvatar,
}: {
  item: SharedContentItem;
  showAvatar: boolean;
}) {
  const TypeIcon = TYPE_ICONS[item.type];

  if (!showAvatar) {
    return (
      <TypeIcon
        aria-label={item.type}
        className="size-5 shrink-0 text-foreground-muted"
      />
    );
  }

  return (
    <span className="relative shrink-0">
      <InitialsAvatar
        name={sharedItemAuthorLabel(item)}
        seed={item.authorEmail ?? String(item.authorUserId)}
        className="size-10 text-xs"
      />
      <span
        aria-label={item.type}
        className={cn(
          'absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center',
          'rounded-full border border-border bg-background'
        )}
      >
        <TypeIcon className="size-3 text-foreground-muted" />
      </span>
    </span>
  );
}

const ProjectSharedItemRow = observer(function ProjectSharedItemRow({
  item,
  isOwn,
  showAvatar,
  testIdPrefix,
  href,
  onOpen,
}: {
  item: SharedContentItem;
  isOwn: boolean;
  showAvatar: boolean;
  testIdPrefix: string;
  href: string;
  onOpen: () => void;
}) {
  const { threads } = useStores();
  const { renameFieldRef, showRenameInputField } = useRenameField();
  const [actionsOpen, setActionsOpen] = useState(false);

  const thread = item.type === 'chat' ? threads.getById(item.id) : undefined;
  const canRename = isOwn && thread !== undefined;

  return (
    // A div rather than a button: the row hosts the rename input and the
    // actions menu, and neither may nest inside a button.
    <div
      role="button"
      tabIndex={0}
      data-testid={`${testIdPrefix}-row-${item.id}`}
      data-readonly={String(!isOwn)}
      className={cn(
        'group relative w-full text-left gap-1.5 px-3 py-2.5',
        'rounded-lg cursor-default',
        'focus-ring-inset',
        'hover:bg-secondary transition-colors',
        actionsOpen && 'bg-secondary'
      )}
      onClick={(event) => {
        if (isRenameInput(event.target)) return;
        onOpen();
      }}
      onKeyDown={(event) => {
        if (isRenameInput(event.target)) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <XStack className="items-center gap-3">
        <ItemTypeAvatar item={item} showAvatar={showAvatar} />
        <YStack className="min-w-0 flex-1 gap-0.5">
          <span
            data-testid={`${testIdPrefix}-title`}
            className="min-w-0 truncate text-sm text-foreground"
          >
            {canRename ? (
              <RenameField
                ref={renameFieldRef}
                value={item.title}
                onRename={(title) => {
                  threads.updateThread(thread, { title });
                  trackEvent('CHAT_TITLE_RENAMED', { chatId: thread.id });
                }}
              />
            ) : (
              item.title
            )}
          </span>
          {/* One line, always: a long first message ellipsizes rather than
              growing the row. */}
          <span className="min-w-0 truncate text-xs text-foreground-muted">
            {item.preview}
          </span>
        </YStack>
        <span
          className={cn(
            'shrink-0 text-xs text-foreground-muted transition-opacity duration-150',
            'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0',
            actionsOpen && 'opacity-0'
          )}
        >
          {formatRelativeDate(item.updatedAt)}
        </span>
      </XStack>
      <ProjectItemRowActions
        item={item}
        isOwn={isOwn}
        itemType={item.type}
        href={href}
        isOpen={actionsOpen}
        onOpenChange={setActionsOpen}
        onRename={() => setTimeout(showRenameInputField, 0)}
      />
    </div>
  );
});
